import React, { useCallback, useEffect, useState } from 'react';
import { RouteComponentProps, useHistory, useLocation } from 'react-router-dom';
import { Alert } from '@clearcover/atlas';
import {
  AnimatedScreen,
  EmptyTable,
  PageHeader,
  Paginate,
  Table,
} from 'Shared/components';
import { useToken } from 'Shared/hooks';
import {
  toCamelCase,
  paramsFromSearchString,
  objectToParams,
  hasScopedPermission,
} from 'Shared/utils';
import { useAuth0 } from 'Auth';
import { StateConfigurationsFilters } from './StateConfigurationsFilters';
import { fetchConfigurations, fetchFilterOptions } from './utils/api';
import {
  transformToTableRows,
  transformToFilterOptions,
} from './utils/transformers';
import { COLUMN_DEFINITIONS, DEFAULT_PAGE_SIZE } from './utils/constants';
import { getDefaultFilters } from './utils/filters';
import {
  ConfigurationResponse,
  FilterType,
  PaginationType,
  FilterOption,
} from './types';

import styles from './styles.module.css';

export const StateConfigurations: React.FC<RouteComponentProps> = () => {
  const token = useToken();
  const history = useHistory();
  const location = useLocation();
  const { user } = useAuth0();

  const hasViewPermission = hasScopedPermission(
    user?.effectiveGrants || [],
    'partner',
    'state_configurations.read'
  );

  useEffect(() => {
    if (!hasViewPermission) {
      history.replace('/');
    }
  }, [hasViewPermission, history]);

  // State
  const [filters, setFilters] = useState<FilterType>(getDefaultFilters());
  const [pagination, setPagination] = useState<PaginationType>({
    limit: DEFAULT_PAGE_SIZE,
    offset: 0,
    total: 0,
  });
  const [sort, setSort] = useState<string>('');
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | 'none'>(
    'none'
  );
  const [data, setData] = useState<ConfigurationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter options
  const [assetTypes, setAssetTypes] = useState<FilterOption[]>([]);
  const [environments, setEnvironments] = useState<FilterOption[]>([]);
  const [locales, setLocales] = useState<FilterOption[]>([]);
  const [productTypes, setProductTypes] = useState<FilterOption[]>([]);
  const [productSegments, setProductSegments] = useState<FilterOption[]>([]);
  const [operatingCompanies, setOperatingCompanies] = useState<FilterOption[]>(
    []
  );

  // Initialize filters from URL parameters on mount (only once)
  useEffect(() => {
    const urlFilters: FilterType = { ...getDefaultFilters() };

    if (location.search) {
      const params = paramsFromSearchString(location.search);

      if (params.assetType) urlFilters.assetType = params.assetType;
      if (params.environment) urlFilters.environment = params.environment;
      if (params.locale) urlFilters.locale = params.locale;
      if (params.productType) urlFilters.productType = params.productType;
      if (params.productSegment)
        urlFilters.productSegment = params.productSegment;
      if (params.operatingCompany)
        urlFilters.operatingCompany = params.operatingCompany;
      if (params.effectiveDate) urlFilters.effectiveDate = params.effectiveDate;
      if (params.activeToday)
        urlFilters.activeToday = params.activeToday === 'true';
    }

    setFilters(urlFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Fetch filter options on mount
  useEffect(() => {
    if (!token) return;

    const loadFilterOptions = async () => {
      try {
        const [
          assetTypesData,
          environmentsData,
          localesData,
          productTypesData,
          productSegmentsData,
          operatingCompaniesData,
        ] = await Promise.all([
          fetchFilterOptions(token, 'assetTypes'),
          fetchFilterOptions(token, 'environments'),
          fetchFilterOptions(token, 'locales'),
          fetchFilterOptions(token, 'productTypes'),
          fetchFilterOptions(token, 'productSegments'),
          fetchFilterOptions(token, 'operatingCompanies'),
        ]);

        setAssetTypes(transformToFilterOptions(assetTypesData));
        setEnvironments(transformToFilterOptions(environmentsData));
        setLocales(transformToFilterOptions(localesData));
        setProductTypes(transformToFilterOptions(productTypesData));
        setProductSegments(transformToFilterOptions(productSegmentsData));
        setOperatingCompanies(transformToFilterOptions(operatingCompaniesData));
      } catch (err) {
        console.error('Error loading filter options:', err);
      }
    };

    loadFilterOptions();
  }, [token]);

  // Fetch configurations
  useEffect(() => {
    if (!token) return;

    const loadConfigurations = async () => {
      setLoading(true);
      setError(null);

      try {
        const page = Math.floor(pagination.offset / pagination.limit);
        const result = await fetchConfigurations(
          token,
          filters,
          page,
          pagination.limit,
          sort
        );

        setData(result);
        setPagination((prev) => ({
          ...prev,
          total: result.total_elements,
        }));
      } catch (err) {
        setError(
          'Unable to load State Configurations. Connect to Clearcover VPN.'
        );
        console.error('Error fetching configurations:', err);
      } finally {
        setLoading(false);
      }
    };

    loadConfigurations();
  }, [token, filters, pagination.offset, pagination.limit, sort]);

  // Handlers
  const onChangeFilters = useCallback(
    (newFilters: FilterType) => {
      setFilters(newFilters);
      setPagination((prev) => ({ ...prev, offset: 0 }));

      // Update URL with new filters
      const queryString = objectToParams(newFilters);
      history.replace({
        pathname: location.pathname,
        search: queryString ? `?${queryString}` : '',
      });
    },
    [history, location.pathname]
  );

  const onOffsetChange = useCallback((limit: number, offset: number) => {
    setPagination((prev) => ({ ...prev, limit, offset }));
  }, []);

  const onLimitChange = useCallback((limit: number) => {
    setPagination((prev) => ({ ...prev, limit, offset: 0 }));
  }, []);

  const handleSort = useCallback(
    (field: string) => {
      let newDirection: 'asc' | 'desc' | 'none';

      if (sortField === field) {
        // Cycle through: none -> asc -> desc -> none
        if (sortDirection === 'none') {
          newDirection = 'asc';
        } else if (sortDirection === 'asc') {
          newDirection = 'desc';
        } else {
          newDirection = 'none';
        }
      } else {
        // New field, start with ascending
        newDirection = 'asc';
      }

      setSortField(newDirection === 'none' ? '' : field);
      setSortDirection(newDirection);

      if (newDirection === 'none') {
        setSort('');
      } else {
        const camelField = toCamelCase(field);
        setSort(`${camelField},${newDirection === 'asc' ? 'asc' : 'desc'}`);
      }
    },
    [sortField, sortDirection]
  );

  // Prepare table data with sortable headers
  const tableHeaders = COLUMN_DEFINITIONS.map((col) => {
    // Don't make the GitHub links column sortable
    if (col.field === 'github_links') {
      return col.label;
    }

    return {
      title: col.label,
      onClick: () => handleSort(col.field),
      sortDirection: sortField === col.field ? sortDirection : 'none',
    };
  });

  const tableColumnWidths = COLUMN_DEFINITIONS.map((col) => col.width);

  // Add class names for styling specific columns
  const tableColumnClassNames = COLUMN_DEFINITIONS.map((col) => {
    if (col.field === 'github_links') {
      return styles.downloadLink;
    }
    return '';
  });

  const tableRows = data?.content ? transformToTableRows(data.content) : [];

  return (
    <AnimatedScreen
      testId="state-configurations-home"
      loading={loading && !data}
      error={error}
    >
      <PageHeader heading="State Configurations" />

      <StateConfigurationsFilters
        filters={filters}
        onChangeFilters={onChangeFilters}
        filterOptions={{
          assetTypes,
          environments,
          locales,
          productTypes,
          productSegments,
          operatingCompanies,
        }}
      />

      {error && (
        <Alert
          message="Unable to load Config Assets. Connect to Clearcover VPN."
          data-testid="error-alert"
        />
      )}

      {!error && tableRows.length > 0 ? (
        <>
          <Table
            headers={tableHeaders}
            columnWidths={tableColumnWidths}
            columnClassNames={tableColumnClassNames}
            rows={tableRows}
            loading={loading}
            hasPagination
            striped
            name="state-configurations-table"
            className="state-configurations-table"
          />
          <Paginate
            total={pagination.total}
            offset={pagination.offset}
            limit={pagination.limit}
            onOffsetChange={onOffsetChange}
            onLimitChange={onLimitChange}
          />
        </>
      ) : (
        !error && !loading && <EmptyTable>No results found</EmptyTable>
      )}
    </AnimatedScreen>
  );
};
